import { memo, useContext, useMemo } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import LabelIcon from '@mui/icons-material/Label';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';

type Props = {
  className?: string;
  count?: number;
  spinner?: boolean; // Whether to show the spinner or not
};

const SPINNER_SIZE = 50;
const BADGE_SIZE = 30; // Standalone badge size when spinner is false

export const SkeletonTags = memo((props: Props) => {
  const { className, count = 3, spinner = true } = props;
  const theming = useContext(AppThemingContext);

  const primaryColor = theming?.palette?.primary?.main ?? '#5F6BFF';
  const skeletonColor = useMemo(() => alpha(primaryColor, 0.1), [primaryColor]);

  const minWidth = 60;
  const maxWidth = 120;
  const height = 28;

  // Adjust gap based on whether spinner is showing
  const gap = spinner ? 12 : 4;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap }} className={className}>
      {spinner ? (
        <LoadingAIBadge primaryColor={primaryColor} spinner={spinner} />
      ) : (
        <AIBadge primaryColor={primaryColor} size={BADGE_SIZE} />
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Array.from({ length: count }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            width={Math.random() * (maxWidth - minWidth) + minWidth}
            height={height}
            sx={{
              borderRadius: '14px',
              bgcolor: skeletonColor,
              animationDuration: '6s',
            }}
          />
        ))}
      </div>
    </div >
  );
});

SkeletonTags.displayName = 'Skeleton.Tags';

type LoadingAIBadgeProps = {
  primaryColor: string;
  spinner: boolean;
};

const LoadingAIBadge = ({ primaryColor, spinner }: LoadingAIBadgeProps) => {
  // Always keep container at SPINNER_SIZE to avoid clipping the badge
  return (
    <div style={{
      position: 'relative',
      width: SPINNER_SIZE,
      height: SPINNER_SIZE,
      flexShrink: 0,
    }}>
      <svg
        viewBox="0 0 100 100"
        style={{
          width: SPINNER_SIZE,
          height: SPINNER_SIZE,
        }}
      >
        <defs>
          <filter id="tag-spinner-shadow">
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="1.5"
              floodColor={primaryColor}
            />
          </filter>
        </defs>
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke={primaryColor}
          strokeWidth="7"
          strokeLinecap="round"
          filter="url(#tag-spinner-shadow)"
          style={{
            transformOrigin: 'center',
            animationName: 'tagSpinnerCycle',
            animationDuration: '1.2s',
            animationIterationCount: 'infinite',
          }}
        />
      </svg>
      <style>{`
            @keyframes tagSpinnerCycle {
            0 % {
              stroke- dasharray: 1 98;
          stroke-dashoffset: -105;
              }
          50% {
            stroke - dasharray: 80 10;
          stroke-dashoffset: -160;
              }
          100% {
            stroke - dasharray: 1 98;
          stroke-dashoffset: -300;
              }
            }
          `}</style>
      <AIBadge primaryColor={primaryColor} size={SPINNER_SIZE} isInsideSpinner={true} />
    </div >
  );
};

type AIBadgeProps = {
  primaryColor: string;
  size: number;
  isInsideSpinner?: boolean;
};

const AIBadge = ({ primaryColor, size, isInsideSpinner = false }: AIBadgeProps) => {
  // Scale everything proportionally to the size
  // Icon size: 60% of container if inside spinner, 120% if standalone
  const iconSize = isInsideSpinner ? size * 0.6 : size * 1.2;
  const iconOffset = (size - iconSize) / 2; // Center the icon
  const fontSize = isInsideSpinner ? size * 0.24 : size * 0.4; // Text size: 24% if inside spinner, 40% if standalone

  return (
    <div style={{
      position: isInsideSpinner ? 'absolute' : 'relative',
      width: isInsideSpinner ? undefined : size,
      height: isInsideSpinner ? undefined : size,
      inset: isInsideSpinner ? 0 : undefined,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <LabelIcon
        style={{
          position: 'absolute',
          left: iconOffset,
          top: iconOffset,
          width: iconSize,
          height: iconSize,
          color: primaryColor,
        }}
      />
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontFamily: 'var(--font-bold)',
        fontSize: `${fontSize}px`,
        color: '#fff',
        zIndex: 1,
        letterSpacing: '0.5px',
      }}>
        AI
      </div>
    </div>
  );
};

